/**
 * PatrolList
 *
 * List container showing all patrols for the current guard/day.
 * Supports search, grouping, and selection.
 *
 * Visual Design:
 * ┌─────────────────────────────┐
 * │ 🔍 Search patrols...       │
 * ├─────────────────────────────┤
 * │ ● Downtown Office          │
 * │   2:00 PM - 4:00 PM       │
 * │   ▶ In Progress           │
 * ├─────────────────────────────┤
 * │ ○ Mall North               │
 * │   4:00 PM - 6:00 PM       │
 * │   Scheduled               │
 * └─────────────────────────────┘
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Collapse,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import classNames from 'classnames';
import styles from './PatrolList.module.css';
import PatrolListItem from './PatrolListItem';

// ============================================
// Types
// ============================================

export interface PatrolGroup {
  customerId: number;
  customerName: string;
  patrols: ScheduleAssignment[];
  patrolCount: number;
}

export interface PatrolListProps {
  /** Patrols to display */
  patrols: ScheduleAssignment[];
  /** Currently selected patrol ID */
  selectedId: number | null;
  /** Callback when a patrol is selected */
  onSelect?: (patrolId: number) => void;
  /** Whether to show search input */
  showSearch?: boolean;
  /** Whether to group patrols by customer */
  groupByCustomer?: boolean;
  /** Loading state */
  isLoading?: boolean;
}

// ============================================
// Utility Functions
// ============================================

/**
 * Groups patrols by customer
 */
function groupPatrolsByCustomer(patrols: ScheduleAssignment[]): PatrolGroup[] {
  if (!Array.isArray(patrols)) return [];

  const groupMap = new Map<number, PatrolGroup>();

  patrols.forEach((patrol) => {
    if (!patrol) return; // Skip null/undefined patrols

    // Use fallback values for missing customer data
    const customerId = patrol.cust_id ?? 0;
    const customerName = patrol.cust_name || 'Unknown Customer';

    if (!groupMap.has(customerId)) {
      groupMap.set(customerId, {
        customerId,
        customerName,
        patrols: [],
        patrolCount: 0,
      });
    }

    const group = groupMap.get(customerId);
    if (group) {
      group.patrols.push(patrol);
      group.patrolCount++;
    }
  });

  // Sort groups by customer name with error handling
  try {
    return Array.from(groupMap.values()).sort((a, b) =>
      (a.customerName || '').localeCompare(b.customerName || '')
    );
  } catch (error) {
    console.error('[PatrolList/sortPatrolGroups] Error sorting patrol groups:', error);
    return Array.from(groupMap.values());
  }
}

/**
 * Filter patrols by search query
 */
function filterPatrols(
  patrols: ScheduleAssignment[],
  query: string
): ScheduleAssignment[] {
  if (!Array.isArray(patrols)) return [];
  if (!query?.trim()) return patrols;

  const searchLower = query.toLowerCase();
  return patrols.filter((patrol) => {
    if (!patrol) return false;
    try {
      return (
        patrol.cust_name?.toLowerCase().includes(searchLower) ||
        patrol.address?.toLowerCase().includes(searchLower) ||
        patrol.title?.toLowerCase().includes(searchLower) ||
        false
      );
    } catch {
      return false;
    }
  });
}

// ============================================
// Sub-components
// ============================================

interface GroupHeaderProps {
  group: PatrolGroup;
  isExpanded: boolean;
  onToggle: () => void;
  matchCount?: number;
}

const GroupHeader: React.FC<GroupHeaderProps> = ({
  group,
  isExpanded,
  onToggle,
  matchCount,
}) => {
  const showMatchCount =
    matchCount !== undefined && matchCount !== group.patrolCount;

  return (
    <Box
      className={classNames(styles.groupHeader, { [styles.expanded]: isExpanded })}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      aria-expanded={isExpanded}
    >
      <Box className={styles.groupHeaderLeft}>
        <IconButton size="small" className={styles.expandIcon}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
        <Typography variant="body2" className={styles.groupName}>
          {group.customerName}
        </Typography>
        <Typography variant="caption" className={styles.groupCount}>
          ({showMatchCount ? `${matchCount}/` : ''}
          {group.patrolCount})
        </Typography>
      </Box>
    </Box>
  );
};

// ============================================
// Main Component
// ============================================

const PatrolList: React.FC<PatrolListProps> = ({
  patrols,
  selectedId,
  onSelect,
  showSearch = true,
  groupByCustomer = false,
  isLoading = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Record<number, boolean>>(
    {}
  );

  // Filter patrols by search
  const filteredPatrols = useMemo(
    () => filterPatrols(patrols, searchQuery),
    [patrols, searchQuery]
  );

  // Group patrols if needed
  const groups = useMemo(() => {
    if (!groupByCustomer) return null;
    return groupPatrolsByCustomer(filteredPatrols);
  }, [filteredPatrols, groupByCustomer]);

  // Handle search change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    []
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  // Toggle group expansion
  const toggleGroup = useCallback((customerId: number) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [customerId]: !prev[customerId],
    }));
  }, []);

  // Check if group is expanded (default to true for first group)
  const isGroupExpanded = useCallback(
    (customerId: number, index: number) => {
      // Auto-expand when searching or if group contains selected patrol
      if (searchQuery.trim()) return true;
      if (expandedGroups[customerId] !== undefined) {
        return expandedGroups[customerId];
      }
      // Default: expand first group
      return index === 0;
    },
    [expandedGroups, searchQuery]
  );

  // Handle patrol selection
  const handlePatrolSelect = useCallback(
    (patrolId: number) => {
      onSelect?.(patrolId);
    },
    [onSelect]
  );

  // Empty state
  if (patrols.length === 0 && !isLoading) {
    return (
      <Box className={styles.listContainer}>
        <Box className={styles.emptyState}>
          <Typography variant="body2" color="textSecondary">
            No patrols scheduled
          </Typography>
        </Box>
      </Box>
    );
  }

  // No search results
  if (filteredPatrols.length === 0 && searchQuery.trim()) {
    return (
      <Box className={styles.listContainer}>
        {showSearch && (
          <Box className={styles.searchContainer}>
            <TextField
              size="small"
              placeholder="Search patrols..."
              value={searchQuery}
              onChange={handleSearchChange}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}
        <Box className={styles.emptyState}>
          <Typography variant="body2" color="textSecondary">
            No patrols match "{searchQuery}"
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.listContainer}>
      {/* Search Input */}
      {showSearch && (
        <Box className={styles.searchContainer}>
          <TextField
            size="small"
            placeholder="Search patrols..."
            value={searchQuery}
            onChange={handleSearchChange}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {/* Patrol List */}
      <Box className={styles.scrollContainer}>
        {groupByCustomer && groups ? (
          // Grouped view
          groups.map((group, index) => (
            <Box key={group.customerId} className={styles.group}>
              <GroupHeader
                group={group}
                isExpanded={isGroupExpanded(group.customerId, index)}
                onToggle={() => toggleGroup(group.customerId)}
              />
              <Collapse in={isGroupExpanded(group.customerId, index)}>
                <Box className={styles.groupContent}>
                  {group.patrols.map((patrol) => (
                    <PatrolListItem
                      key={patrol.schedule_assignment_id}
                      patrol={patrol}
                      isSelected={selectedId === patrol.schedule_assignment_id}
                      onClick={handlePatrolSelect}
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          ))
        ) : (
          // Flat list view
          filteredPatrols.map((patrol) => (
            <PatrolListItem
              key={patrol.schedule_assignment_id}
              patrol={patrol}
              isSelected={selectedId === patrol.schedule_assignment_id}
              onClick={handlePatrolSelect}
            />
          ))
        )}
      </Box>

      {/* Summary */}
      <Box className={styles.listSummary}>
        <Typography variant="caption" color="textSecondary">
          {filteredPatrols.length} patrol
          {filteredPatrols.length !== 1 ? 's' : ''}
          {searchQuery.trim() && ` matching "${searchQuery}"`}
        </Typography>
      </Box>
    </Box>
  );
};

export default PatrolList;
